import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Send, Mail, MapPin, Phone } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import Logo from "@/components/brand/Logo";


// Minimal inline WhatsApp icon (lucide doesn't ship a brand-whatsapp)
function WhatsAppIcon({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M12.05 2.25c-5.4 0-9.78 4.38-9.78 9.78 0 1.72.45 3.4 1.3 4.88L2.25 21.75l4.98-1.3a9.76 9.76 0 0 0 4.82 1.27c5.4 0 9.78-4.38 9.78-9.78 0-2.61-1.02-5.07-2.87-6.92a9.73 9.73 0 0 0-6.91-2.77zm0 17.82a8 8 0 0 1-4.08-1.12l-.29-.17-2.96.77.79-2.88-.19-.3a8.05 8.05 0 0 1-1.23-4.27 8.05 8.05 0 0 1 13.75-5.69 7.99 7.99 0 0 1 2.36 5.69c0 4.42-3.6 8-8.15 8.02zm4.47-6.02c-.24-.12-1.43-.71-1.66-.79-.22-.08-.38-.12-.55.12-.16.24-.62.79-.76.95-.14.16-.28.18-.52.06-.24-.12-1.02-.37-1.95-1.19-.72-.64-1.2-1.43-1.34-1.67-.14-.24-.02-.37.1-.49.1-.1.24-.28.36-.42.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.55-1.32-.75-1.81-.2-.48-.4-.41-.55-.42h-.47c-.16 0-.42.06-.64.3-.22.24-.84.82-.84 2 0 1.18.86 2.32.98 2.48.12.16 1.69 2.58 4.09 3.62.57.25 1.01.4 1.36.51.57.18 1.09.15 1.5.09.46-.07 1.43-.58 1.63-1.14.2-.57.2-1.05.14-1.14-.06-.09-.22-.15-.46-.27z" />
    </svg>
  );
}

export default function Footer() {
  const { t, lang } = useLang();
  const year = new Date().getFullYear();

  const legalText = {
    ar: { title: "قانوني", privacy: "سياسة الخصوصية", terms: "شروط الخدمة" },
    en: { title: "Legal", privacy: "Privacy Policy", terms: "Terms of Service" },
    tr: { title: "Yasal", privacy: "Gizlilik Politikası", terms: "Hizmet Şartları" },
    "zh-CN": { title: "法律", privacy: "隐私政策", terms: "服务条款" }
  };

  const legal = legalText[lang] || legalText.en;

  const socials = [
    // Each network lights up in its own brand colour on hover / focus.
    { Icon: Facebook, label: "Facebook", href: "https://www.facebook.com/miskcargo", id: "fb", hover: "hover:bg-[#1877F2] hover:border-[#1877F2] focus-visible:bg-[#1877F2]" },
    { Icon: Instagram, label: "Instagram", href: "https://www.instagram.com/miskcargo.tr", id: "ig", hover: "hover:border-[#E1306C] hover:bg-[radial-gradient(circle_at_30%_110%,#FEDA75_0%,#FA7E1E_25%,#D62976_55%,#962FBF_80%,#4F5BD5_100%)] focus-visible:bg-[#E1306C]" },
    { Icon: Send, label: "Telegram", href: "https://t.me/pMpv2lkJs78zOTVk", id: "tg", hover: "hover:bg-[#26A5E4] hover:border-[#26A5E4] focus-visible:bg-[#26A5E4]" },
    { Icon: WhatsAppIcon, label: "WhatsApp", href: "https://wa.me/905521627900", id: "wa", hover: "hover:bg-[#25D366] hover:border-[#25D366] focus-visible:bg-[#25D366]" },
  ];

  return (
    <footer
      data-testid="site-footer"
      className="relative overflow-hidden bg-slate-950 text-slate-200 border-t border-white/10"
    >
      {/* Top accent route */}
      <div aria-hidden className="absolute inset-x-0 top-0 h-[1px]">
        <div className="h-full w-full bg-gradient-to-r from-transparent via-green-500/60 to-transparent" />
      </div>

      {/* Decorative circles */}
      <div aria-hidden className="absolute -top-40 -end-40 w-96 h-96 rounded-full bg-green-500/5 blur-3xl" />
      <div aria-hidden className="absolute -bottom-32 -start-32 w-96 h-96 rounded-full bg-sky-500/5 blur-3xl" />

      <div className="container-x relative py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="md:col-span-4">
            <Link to="/" className="mk-logo-link inline-flex" dir="ltr" aria-label="Misk Cargo home">
              <Logo tone="dark" className="h-12 w-auto" />
            </Link>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-400">
              {t.footer.tagline}
            </p>

            {/* Socials */}
            <div className="mt-6">
              <div className="text-sm font-semibold text-slate-400 mb-3">
                {t.contact.followUs}
              </div>
              <div className="flex items-center gap-3">
                {socials.map(({ Icon, label, href, id, hover }) => (
                  <a
                    key={id}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`social-${id}`}
                    aria-label={label}
                    className={`group w-11 h-11 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white transition-[background-color,border-color,transform] duration-300 hover:-translate-y-0.5 ${hover}`}
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Quick links */}
          <div className="md:col-span-2">
            <div className="text-sm font-semibold text-slate-400 mb-4">
              {t.footer.quickLinks}
            </div>
            <ul className="space-y-3 text-sm">
              <li>
                <Link data-testid="footer-home" to="/" className="text-slate-300 hover:text-green-400 transition-colors">
                  {t.nav.home}
                </Link>
              </li>
              <li>
                <Link data-testid="footer-about" to="/about" className="text-slate-300 hover:text-green-400 transition-colors">
                  {t.nav.about}
                </Link>
              </li>
              <li>
                <Link data-testid="footer-services" to="/services" className="text-slate-300 hover:text-green-400 transition-colors">
                  {t.nav.services}
                </Link>
              </li>
              <li>
                <Link data-testid="footer-contact" to="/contact" className="text-slate-300 hover:text-green-400 transition-colors">
                  {t.nav.contact}
                </Link>
              </li>
              <li>
                <Link data-testid="footer-faq" to="/faq" className="text-slate-300 hover:text-green-400 transition-colors">
                  FAQ
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div className="md:col-span-2">
            <div className="text-sm font-semibold text-slate-400 mb-4">
              {legal.title}
            </div>
            <ul className="space-y-3 text-sm">
              <li>
                <Link data-testid="footer-privacy" to="/privacy-policy" className="text-slate-300 hover:text-green-400 transition-colors">
                  {legal.privacy}
                </Link>
              </li>
              <li>
                <Link data-testid="footer-terms" to="/terms-of-service" className="text-slate-300 hover:text-green-400 transition-colors">
                  {legal.terms}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <div className="text-sm font-semibold text-slate-400 mb-4">
              {t.footer.contactUs}
            </div>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-0.5 text-green-400 flex-shrink-0" />
                <div className="flex flex-col gap-1">
                  <a
                    href="mailto:operations@miskkargo.com"
                    data-testid="footer-email-operations"
                    className="text-slate-300 hover:text-green-400 transition-colors break-all"
                  >
                    operations@miskkargo.com
                  </a>
                  <a
                    href="mailto:sales@miskkargo.com"
                    data-testid="footer-email-sales"
                    className="text-slate-300 hover:text-green-400 transition-colors break-all"
                  >
                    sales@miskkargo.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-0.5 text-green-400 flex-shrink-0" />
                <a
                  href="https://wa.me/905521627900"
                  data-testid="footer-phone"
                  className="text-slate-300 hover:text-green-400 transition-colors"
                  dir="ltr"
                >
                  <bdi>+90 552 162 7900</bdi>
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-green-400 flex-shrink-0" />
                <span className="text-slate-400">
                  {t.cities.istanbul} · {t.cities.damascus} · {t.cities.aleppo}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col items-center justify-center gap-4 text-xs text-slate-500">
          <p>© {year} Misk Cargo. {t.footer.rights}.</p>
        </div>
      </div>
    </footer>
  );
}
